import {Helmet} from '@dr.pogodin/react-helmet';
import {isFeatureEnabled} from 'feature-flags';
import i18next from 'i18next';
import {DataStatus, showNotification, Wizard} from 'platform/components';
import {match, Pattern} from 'ts-pattern';
import {ZodSchema} from 'zod';

import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useWatch} from 'react-hook-form';
import {useLocation} from 'react-router-dom';

import {isNil, omit} from 'ramda';
import {isNilOrEmpty, isNotNil} from 'ramda-adjunct';

import {useGetFileByIdQuery} from '@dms/api/base';
import {
  ConditionEnum,
  useGetActiveSaleVehicleQuery,
  useGetVehicleQuery,
  useUpdateVehicleMutation,
  VehicleTypeEnum,
} from '@dms/api/vehicle';
import {useGetServiceVehicleV2Query, usePatchServiceVehicleMutation} from '@dms/api/workshop';
import {featureFlags} from '@dms/feature-flags';
import {
  VehicleForm as VFKit,
  toBaseVehicle,
  BaseVehicle,
  toAPIVehicle,
  useVehicleFormContext,
  useWizardTabRouting,
  useVehicleVariantSync,
  useUpdateVehicleType,
  useBaseVehicleWizard,
  useGetCarFeatures,
  SaleVehicle,
  SaleVehicleValidationSchema,
  useVehicleVariant,
  RentalVehicleValidationSchema,
  ServiceVehicleValidationSchema,
  useFormKeyboardNavigation,
  useVehicleDocumentSync,
  resolveInitialWarrantyDuration,
  useIsVehicleQualitySealShown,
} from '@dms/features/vehicle-forms-kit';
import i18n from '@dms/i18n';
import {vehiclesRoutes} from '@dms/routes';
import {
  formatDocumentTitle,
  FullScreenModal,
  handleApiError,
  queryParams,
  useAdPlatformsVehicleUpdateDialog,
  useMandatoryFields,
  FieldAvailabilityContext,
} from '@dms/shared';

import {composePath, isSafeUrl, useNavigate, useQueryState, useRequiredParams} from 'shared/web';

import {BasicInformation} from '../(sections)/BasicInformation';
import {Features} from '../(sections)/Features';
import {VehicleData} from '../(sections)/VehicleData';
import {usePatchSaleFields} from '../hooks/usePatchSaleFields';
import {resolveQualitySealPatchValue} from '../utils/resolveQualitySealPatchValue';

interface EditBaseVehicleFormProps {
  isUpdating: boolean;
  onSave: (values: BaseVehicle) => void;
  onVehicleTypeChange: (type: VehicleTypeEnum, condition?: ConditionEnum) => void;
  onFuelTypeChange: (fuelTypeCondition: FieldAvailabilityContext) => void;
  onDiscard: () => void;
  vehicleId: string;
}

const EditBaseVehicleForm = ({
  onSave,
  onDiscard,
  onVehicleTypeChange,
  onFuelTypeChange,
  isUpdating,
  vehicleId,
}: EditBaseVehicleFormProps) => {
  const {control, formApi, vehicleVariant} = useVehicleFormContext();

  // Sync vehicle variant (sale / rental / service) data with XState machine
  useVehicleVariantSync(vehicleId);

  const wizardSteps = useBaseVehicleWizard({
    basicInformation: <BasicInformation />,
    vehicleData: <VehicleData />,
    features: <Features />,
  });

  const vehicleTypeWatch = useWatch({
    control,
    name: 'type',
  });

  const vehicleConditionWatch = useWatch({
    control,
    name: 'condition',
  });

  const fuelTypeWatch = useWatch<BaseVehicle, 'fuelType'>({
    control,
    name: 'fuelType',
  });

  const hybridTypeWatch = useWatch<BaseVehicle, 'hybridType'>({
    control,
    name: 'hybridType',
  });

  useEffect(
    function notifyVehicleTypeChange() {
      if (vehicleVariant?.hasSaleVehicle) {
        onVehicleTypeChange(
          (vehicleTypeWatch?.[0] as VehicleTypeEnum) ?? '',
          vehicleConditionWatch?.[0] as ConditionEnum
        );
      }
    },
    [onVehicleTypeChange, vehicleTypeWatch, vehicleConditionWatch, vehicleVariant]
  );

  useEffect(
    function notifyFuelTypeChange() {
      // Skip the initial call before the form hydrates default values
      if (fuelTypeWatch === undefined && hybridTypeWatch === undefined) {
        return;
      }
      if (vehicleVariant?.hasSaleVehicle) {
        onFuelTypeChange({
          fuelType: fuelTypeWatch?.at(0),
          hybridType: hybridTypeWatch?.at(0),
        });
      }
    },
    [onFuelTypeChange, fuelTypeWatch, hybridTypeWatch, vehicleVariant]
  );

  const tabStepMap = useMemo(
    () => ({
      'basic-information': 0,
      'vehicle-data': 1,
      features: 2,
    }),
    []
  );

  // Configure tab routing with wizard ref control
  const {wizardRef, onStepChange} = useWizardTabRouting({
    tabStepMap,
    tabRoutePattern: vehiclesRoutes.editV2Tab,
    defaultTab: 'basic-information',
  });

  const {onKeyDownCapture} = useFormKeyboardNavigation({
    wizardRef,
    onStepChange,
  });

  return (
    <FullScreenModal
      data-testid="editBaseVehicle"
      headline={i18n.t('entity.vehicle.labels.editVehicle')}
      backgroundColor="general.white"
      scrollable={false}
      actions={[
        {
          title: i18n.t('general.labels.discard'),
          variant: 'secondary',
          isLoading: isUpdating,
          onClick: onDiscard,
        },
        {
          title: i18n.t('general.actions.save'),
          isLoading: isUpdating,
          onClick: () => {
            formApi.handleSubmit(
              (values) => {
                onSave(values);
              },
              (error) => {
                if (i18next.exists('errors.invalid_submit')) {
                  showNotification.warning(i18next.t('errors.invalid_submit'));
                }

                if (process.env.ENV_TYPE === 'dev') {
                  console.error(error);
                }
              }
            )();
          },
        },
      ]}
    >
      <Wizard
        ref={wizardRef}
        orientation="horizontal"
        steps={wizardSteps}
        height="100%"
        onStepChange={onStepChange}
        onKeyDownCapture={onKeyDownCapture}
      />
    </FullScreenModal>
  );
};

export function EditBaseVehicle() {
  const navigate = useNavigate();
  const location = useLocation();
  const {id: vehicleId} = useRequiredParams();
  const [returnUrl] = useQueryState(queryParams.RETURN_URL);

  const navigateBack = useCallback(() => {
    if (returnUrl && isSafeUrl(returnUrl)) {
      navigate(returnUrl);
      return;
    }

    // React Router sets location.key to 'default' for the initial page load.
    // If the key is 'default', the user opened this URL directly (no in-app history to go back to),
    // so we fall back to the vehicle detail page instead of navigate(-1).
    if (location.key === 'default') {
      navigate(composePath(vehiclesRoutes.detail, {params: {id: vehicleId}}));
    } else {
      navigate(-1);
    }
  }, [returnUrl, location.key, navigate, vehicleId]);
  const {
    generateSchema,
    isMandatoryFieldsEnabled,
    isLoading: isMandatoryFieldsLoading,
  } = useMandatoryFields();

  const {
    data: vehicle,
    isLoading: isVehicleLoading,
    isError: isVehicleError,
  } = useGetVehicleQuery(
    {vehicleId},
    {
      skip: isNilOrEmpty(vehicleId),
    }
  );

  const [
    {hasSaleVehicle, hasRentalVehicle, hasServiceVehicle},
    {isLoading: isVehicleVariantLoading},
  ] = useVehicleVariant(vehicleId);

  // Fetch battery certificate file metadata if certificateFileId exists
  const {data: certificateFile, isLoading: isCertificateFileLoading} = useGetFileByIdQuery(
    {fileId: vehicle?.certificateFileId ?? ''},
    {skip: isNilOrEmpty(vehicle?.certificateFileId)}
  );

  const currentContextRef = useRef<FieldAvailabilityContext>({
    vehicleType: vehicle?.type as VehicleTypeEnum | undefined,
  });

  const schema = match([hasRentalVehicle, hasServiceVehicle, hasSaleVehicle])
    .with([true, Pattern.boolean, Pattern.boolean], () => RentalVehicleValidationSchema)
    .with([Pattern.boolean, true, false], () => ServiceVehicleValidationSchema)
    .otherwise(() => generateSchema(SaleVehicleValidationSchema, currentContextRef.current));

  const [formSchema, setFormSchema] = useState<ZodSchema<BaseVehicle>>(schema);

  const [patchSaleVehicleFields] = usePatchSaleFields();
  const isQualitySealShown = useIsVehicleQualitySealShown(vehicleId);
  const [updateVehicleAds] = useAdPlatformsVehicleUpdateDialog();

  const [patchServiceVehicleMutation] = usePatchServiceVehicleMutation();

  const [getCarFeatures] = useGetCarFeatures();

  const [updateVehicleMutation, {isLoading: isUpdating}] = useUpdateVehicleMutation();
  const {syncDocuments} = useVehicleDocumentSync();
  const [updateVehicleType] = useUpdateVehicleType(vehicleId, vehicle?.type);

  const handleVehicleTypeChange = useCallback(
    (type: VehicleTypeEnum, condition?: ConditionEnum) => {
      currentContextRef.current = {...currentContextRef.current, vehicleType: type};

      if (hasSaleVehicle) {
        const newSchema = generateSchema(
          SaleVehicleValidationSchema,
          currentContextRef.current,
          condition
        );
        return setFormSchema(newSchema);
      }

      if (hasRentalVehicle) {
        return setFormSchema(RentalVehicleValidationSchema);
      }

      if (hasServiceVehicle && !hasSaleVehicle) {
        return setFormSchema(ServiceVehicleValidationSchema);
      }
    },
    [generateSchema, hasSaleVehicle, hasRentalVehicle, hasServiceVehicle]
  );

  const handleFuelTypeChange = useCallback(
    (fuelTypeCondition: FieldAvailabilityContext) => {
      currentContextRef.current = {...currentContextRef.current, ...fuelTypeCondition};

      if (hasSaleVehicle) {
        const newSchema = generateSchema(SaleVehicleValidationSchema, currentContextRef.current);
        setFormSchema(newSchema);
      }
    },
    [generateSchema, hasSaleVehicle]
  );

  useEffect(
    function applyLoadedVehicleType() {
      if (vehicle && vehicle?.type) {
        handleVehicleTypeChange(
          vehicle.type as VehicleTypeEnum,
          vehicle.condition as ConditionEnum
        );
      }
    },
    [handleVehicleTypeChange, vehicle]
  );

  const {
    salePresetId,
    vehicleSourceCodeId,
    warrantyProgramId,
    warrantyDuration,
    qualitySealKey,
    defaultWarrantyDuration,
    isLoading: isSaleVehicleQueryLoading,
  } = useGetActiveSaleVehicleQuery(
    {vehicleId},
    {
      skip: isNilOrEmpty(vehicleId),
      selectFromResult: ({data, isLoading}) => ({
        vehicleSourceCodeId: data?.vehicleSourceCodeId,
        salePresetId: data?.salePresetId,
        warrantyProgramId: data?.warrantyProgramId,
        warrantyDuration: data?.warrantyDuration,
        qualitySealKey: data?.qualitySealKey,
        defaultWarrantyDuration: data?.defaultWarrantyDuration,
        isLoading,
      }),
    }
  );

  // On the edit form the warranty duration is pre-filled from the per-vehicle default whenever no
  // program is assigned.
  const initialWarrantyDuration = resolveInitialWarrantyDuration({
    warrantyProgramId,
    warrantyDuration,
    defaultWarrantyDuration,
  });

  const {
    serviceVehicleId,
    vehicleClassId,
    isLoading: isServiceVehicleQueryLoading,
  } = useGetServiceVehicleV2Query(
    {
      vehicleId,
    },
    {
      skip: isNilOrEmpty(vehicleId),
      selectFromResult: ({data, isLoading}) => ({
        serviceVehicleId: data?.serviceVehicleId,
        vehicleClassId: data?.vehicleClassId,
        isLoading,
      }),
    }
  );

  const isSalesWarrantyEnabled = isFeatureEnabled(featureFlags.SALES_WARRANTY);

  const handleSave = async (values: SaleVehicle) => {
    const sourcingData = await getCarFeatures(values);
    const existingFeatures = vehicle?.features || null;

    const apiPayload = toAPIVehicle(
      omit(['vehicleSourceCodeId', 'salePresetId', 'qualitySealKey'], values),
      existingFeatures,
      sourcingData,
      vehicle
    );

    const redirect = async () => {
      if (hasSaleVehicle) {
        const result = await updateVehicleAds(vehicleId, undefined, navigateBack);
        if (result === false) {
          navigateBack();
        }
      } else {
        navigateBack();
      }
    };

    const onUpdate = () =>
      updateVehicleMutation({
        vehicleId,
        updateVehicleRequestBody: apiPayload,
        validateMandatoryFields:
          isMandatoryFieldsEnabled(values.condition?.[0] ?? undefined) && hasSaleVehicle,
      })
        .unwrap()
        .then(() => {
          const currentSourceCodeId = (values as SaleVehicle).vehicleSourceCodeId;
          const currentSalePresetId = (values as SaleVehicle).salePresetId;
          const currentWarrantyProgramId = (values as SaleVehicle).warrantyProgramId;
          const currentWarrantyDuration = (values as SaleVehicle).warrantyDuration;
          const currentQualitySealKey = values.qualitySealKey;

          const getPatchValue = <T,>(current: T | undefined, original: T | undefined) => {
            if (current !== undefined) {
              return current;
            }
            if (original !== undefined) {
              return null;
            }
            return undefined;
          };

          const patchSourceCodeId = getPatchValue(currentSourceCodeId, vehicleSourceCodeId);
          const patchSalePresetId =
            currentSalePresetId !== salePresetId
              ? getPatchValue(currentSalePresetId, salePresetId)
              : undefined;
          const patchQualitySealKey = resolveQualitySealPatchValue({
            currentQualitySealKey,
            originalQualitySealKey: qualitySealKey,
            isQualitySealShown,
            wasQualitySealChangedByUser: values.isQualitySealManualOverride ?? false,
            hasSalePresetChanged: patchSalePresetId !== undefined,
          });
          const patchWarrantyProgramId =
            !hasRentalVehicle && isSalesWarrantyEnabled
              ? getPatchValue(currentWarrantyProgramId, warrantyProgramId)
              : undefined;
          const patchWarrantyDuration =
            !hasRentalVehicle && isSalesWarrantyEnabled
              ? getPatchValue(currentWarrantyDuration, warrantyDuration)
              : undefined;

          if (
            patchSourceCodeId !== undefined ||
            patchSalePresetId !== undefined ||
            patchQualitySealKey !== undefined ||
            patchWarrantyProgramId !== undefined ||
            patchWarrantyDuration !== undefined
          ) {
            // eslint-disable-next-line eslint-js/no-restricted-syntax
            return patchSaleVehicleFields(vehicleId, {
              vehicleSourceCodeId: patchSourceCodeId,
              salePresetId: patchSalePresetId,
              qualitySealKey: patchQualitySealKey,
              warrantyProgramId: patchWarrantyProgramId,
              warrantyDuration: patchWarrantyDuration,
            });
          }
          return Promise.resolve();
        })
        .then(() => {
          const nextVehicleClassId = values.vehicleClassId ?? null;
          const originalVehicleClassId = vehicleClassId ?? null;
          if (serviceVehicleId && nextVehicleClassId !== originalVehicleClassId) {
            return patchServiceVehicleMutation({
              serviceVehicleId,
              patchServiceVehicleRequestBody: {
                vehicleClassId: nextVehicleClassId,
              },
            })
              .unwrap()
              .then(() => undefined)
              .catch(handleApiError);
          }
          return Promise.resolve();
        })
        // Sync battery certificate document
        .then(async () => {
          await syncDocuments({
            vehicleId,
            contextTarget: 'vehicle',
            fileFields: [
              {
                fieldValue: values.batteryCertificateDocument,
                documentNote: i18n.t('entity.vehicle.labels.batteryCertificate'),
              },
            ],
          });
        })
        .then(redirect);

    let returnPromise: Promise<void> | null = null;

    if (apiPayload.type !== vehicle?.type) {
      /**
       * We will let this dialog be commented out for now in case we will want to revert back
       */
      // openConfirmDialog({
      //   text: i18n.t('entity.vehicle.labels.vehicleTypeUpdatedSuccessfully'),
      //   onConfirm: () => {
      // eslint-disable-next-line eslint-js/no-restricted-syntax
      returnPromise = updateVehicleType(apiPayload.type).then(onUpdate).catch(handleApiError);
      // },
      // });
    } else {
      // eslint-disable-next-line eslint-js/no-restricted-syntax
      returnPromise = onUpdate().catch(handleApiError);
    }

    return returnPromise;
  };

  const isLoading =
    isMandatoryFieldsLoading ||
    isVehicleLoading ||
    isVehicleVariantLoading ||
    isSaleVehicleQueryLoading ||
    isServiceVehicleQueryLoading ||
    isCertificateFileLoading;
  const isError = isVehicleError;

  const defaultValues = isNotNil(vehicle) ? toBaseVehicle(vehicle, certificateFile) : undefined;

  return (
    <>
      <Helmet>
        <title>
          {formatDocumentTitle(
            vehicle?.title ?? i18n.t('entity.vehicle.labels.vehicle'),
            i18n.t('entity.vehicle.labels.pageTitle')
          )}
        </title>
      </Helmet>
      <DataStatus
        isLoading={isLoading}
        isError={isError}
        isEmpty={isNil(defaultValues)}
        emptyMessage={i18n.t('page.vehicle.notifications.notFound')}
        action={{
          title: i18n.t('general.actions.back'),
          leftIcon: 'navigation/chevron_left',
          onClick: navigateBack,
        }}
        data-testid="editBaseVehicle-notFound"
      >
        <VFKit.Form
          mode="onBlur"
          experimentalZodSchema={formSchema}
          showMandatoryFields={hasSaleVehicle}
          defaultValues={{
            ...defaultValues,
            ...(hasSaleVehicle && {salePresetId, vehicleSourceCodeId}),
            ...(hasSaleVehicle && {qualitySealKey}),
            isQualitySealManualOverride: false,
            ...(!hasRentalVehicle &&
              hasSaleVehicle &&
              isSalesWarrantyEnabled && {
                warrantyProgramId,
                warrantyDuration: initialWarrantyDuration,
              }),
            ...(hasServiceVehicle && {vehicleClassId}),
          }}
        >
          <EditBaseVehicleForm
            onVehicleTypeChange={handleVehicleTypeChange}
            onFuelTypeChange={handleFuelTypeChange}
            onSave={handleSave}
            onDiscard={navigateBack}
            isUpdating={isUpdating}
            vehicleId={vehicleId}
          />
        </VFKit.Form>
      </DataStatus>
    </>
  );
}
